import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Kurze Einblendungen statt alert(): Sie halten den Arbeitsfluss nicht an,
// lassen sich übereinander stapeln und verschwinden von selbst.
// Fehler bleiben länger stehen als Erfolgsmeldungen.
const HinweisContext = createContext(null);

const DAUER = { erfolg: 4000, info: 5000, fehler: 9000 };
const FARBEN = {
  erfolg: { bg: '#e8f5e9', rand: '#a5d6a7', text: '#1b5e20', zeichen: '✓' },
  info:   { bg: '#e3f2fd', rand: '#90caf9', text: '#0d47a1', zeichen: 'ℹ' },
  fehler: { bg: '#fde8e8', rand: '#f5b8b8', text: '#b71c1c', zeichen: '⚠' },
};

// Zugang ohne Hook: So lässt sich auch aus Hilfsfunktionen und verschachtelten
// Dialogen melden, ohne jede davon zu einer Komponente mit Hook zu machen.
let anmelden = null;
export const hinweis = {
  erfolg: (text, aktion) => (anmelden ? anmelden('erfolg', text, aktion) : undefined),
  info:   (text, aktion) => (anmelden ? anmelden('info', text, aktion) : undefined),
  // Ohne Provider (z.B. Login-Seite) lieber ein Fenster als gar keine Rückmeldung
  fehler: (text, aktion) => (anmelden ? anmelden('fehler', text, aktion) : window.alert(text)),
};

export function HinweiseProvider({ children }) {
  const [liste, setListe] = useState([]);

  const entfernen = useCallback((id) => setListe(l => l.filter(h => h.id !== id)), []);

  const zeigen = useCallback((art, text, aktion) => {
    if (!text) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setListe(l => [...l.slice(-4), { id, art, text: String(text), aktion }]);
    window.setTimeout(() => entfernen(id), DAUER[art] || 5000);
    return id;
  }, [entfernen]);

  const api = useMemo(() => ({
    erfolg: (text, aktion) => zeigen('erfolg', text, aktion),
    info:   (text, aktion) => zeigen('info', text, aktion),
    fehler: (text, aktion) => zeigen('fehler', text, aktion),
    schliessen: entfernen,
  }), [zeigen, entfernen]);

  useEffect(() => {
    anmelden = zeigen;
    return () => { anmelden = null; };
  }, [zeigen]);

  return (
    <HinweisContext.Provider value={api}>
      {children}
      <div style={{
        position: 'fixed', right: 16, bottom: 16, zIndex: 4000,
        display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'min(420px, calc(100vw - 32px))',
      }} aria-live="polite">
        {liste.map(h => {
          const f = FARBEN[h.art] || FARBEN.info;
          return (
            <div key={h.id} role="status"
              style={{
                background: f.bg, border: `1px solid ${f.rand}`, color: f.text,
                borderRadius: 10, padding: '0.7rem 0.9rem', boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
                display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.88rem', lineHeight: 1.35,
              }}>
              <span style={{ fontWeight: 700 }}>{f.zeichen}</span>
              <span style={{ flex: 1, whiteSpace: 'pre-line' }}>{h.text}</span>
              {h.aktion && (
                <button onClick={() => { h.aktion.aufruf(); entfernen(h.id); }}
                  style={{
                    background: 'none', border: `1px solid ${f.rand}`, color: f.text, borderRadius: 6,
                    padding: '2px 8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  {h.aktion.titel}
                </button>
              )}
              <button onClick={() => entfernen(h.id)} aria-label="Hinweis schließen"
                style={{ background: 'none', border: 'none', color: f.text, cursor: 'pointer', fontSize: '1rem', opacity: 0.6, lineHeight: 1 }}>
                ×
              </button>
            </div>
          );
        })}
      </div>
    </HinweisContext.Provider>
  );
}

// Außerhalb des Providers (z.B. in Tests) still bleiben statt abstürzen
const STUMM = { erfolg: () => {}, info: () => {}, fehler: () => {}, schliessen: () => {} };
export const useHinweis = () => useContext(HinweisContext) || STUMM;
export default HinweiseProvider;
